import { intlShape, FormattedMessage, FormattedNumber } from 'react-intl';
import { connect } from 'react-redux';
import autobind from 'core-decorators/lib/autobind';
import classnames from 'classnames';
import clamp from 'text-overflow-clamp';
import * as dealInfoHelpers from 'shared/utils/deal-info-helpers';
import getUrlToVideo from 'shared/utils/get-url-to-video';
import bi from 'shared/bi/bi';

import { WixConnect } from 'shared/utils/wix-connect';
import { notForPreview } from 'widget/utils/not-for-preview';
import paymentsHelper from 'widget/utils/payments';

import { getVideosGroupedByIds } from 'shared/selectors/videos';
import { getAllSettings } from 'shared/selectors/app-settings';
import { getCurrentSiteUser } from 'widget/selectors/current-site-user';

import { closeModal } from 'shared/components/modal/modal';
import { getChannelForWidget } from 'shared/selectors/channels';
import ResizedImage from 'shared/components/resized-image/resized-image';
import Icon from 'shared/components/icon/icon';
import ButtonsGroup from 'widget/components/button/buttons-group';
import AccountForm from 'widget/components/account/account-form';

import { PAYMENT_PERMS, PAYMENT_TYPE } from 'shared/constants/payments';

import styles from './payment-form.styl';

import translations from './translations';


const COVER_WIDTH = 314;
const COVER_HEIGHT = 177;

const biInfoEventNames = {
  [PAYMENT_TYPE.MEMBER_ONLY]: 'video.memberOnlyInfo.fullscreenModal.rendered',
  [PAYMENT_TYPE.PURCHASE]: 'video.purchaseInfo.fullscreenModal.rendered',
  [PAYMENT_TYPE.RENT]: 'video.purchaseInfo.fullscreenModal.rendered',
  [PAYMENT_TYPE.SUBSCRIPTION]: 'channel.subscribeInfo.fullscreenModal.rendered',
};

const biPurchaseEventNames = {
  [PAYMENT_TYPE.PURCHASE]: 'video.purchase.requested',
  [PAYMENT_TYPE.RENT]: 'video.purchase.requested',
  [PAYMENT_TYPE.SUBSCRIPTION]: 'channel.subscribe.requested',
};

@connect(state => ({
  channelData: getChannelForWidget(state),
  videoByIds: getVideosGroupedByIds(state),
  currentSiteUser: getCurrentSiteUser(state),
  appSettings: getAllSettings(state),
}))
@WixConnect(styles)
export default class PaymentForm extends React.Component {

  static propTypes = {
    paymentType: React.PropTypes.oneOf([
      PAYMENT_TYPE.SUBSCRIPTION,
      PAYMENT_TYPE.PURCHASE,
      PAYMENT_TYPE.RENT,
      PAYMENT_TYPE.MEMBER_ONLY,
    ]),
    channelData: React.PropTypes.object,
    videoByIds: React.PropTypes.object,
    currentSiteUser: React.PropTypes.object,
    appSettings: React.PropTypes.object,
    biEvent: React.PropTypes.func,
    className: React.PropTypes.string,
    videoId: React.PropTypes.string,
    onUserLogin: React.PropTypes.func,
    onUserLogout: React.PropTypes.func,
    onBeforePageRefresh: React.PropTypes.func,

    intl: intlShape,
  };

  static defaultProps = {
    onUserLogin: _.noop,
    onUserLogout: _.noop,
    onBeforePageRefresh: _.noop,
  };

  constructor(props) {
    super(props);

    let { paymentType } = this.props;

    if (!this.getDealInfoByType(paymentType)) {
      if (paymentType === PAYMENT_TYPE.PURCHASE) {
        paymentType = PAYMENT_TYPE.RENT;
      } else if (paymentType === PAYMENT_TYPE.RENT) {
        paymentType = PAYMENT_TYPE.PURCHASE;
      } else {
        paymentType = null;
      }
    }

    this.state = {
      paymentType,
    };

    if (!this.getDealInfoByType(paymentType)) {
      closeModal();
    }
  }

  componentDidMount() {
    const { paymentType } = this.props;

    this.clamp();

    window.addEventListener('message', this.paymentComplete, false);
    bi.logWidgetSystem(biInfoEventNames[paymentType]);
  }

  componentDidUpdate() {
    this.clamp();
  }

  componentWillUnmount() {
    window.removeEventListener('message', this.paymentComplete, false);
  }

  clamp() {
    clamp(this.titleNode, { lineClamp: 2 });
  }

  @autobind
  paymentComplete(event) {
    const isCompleted = _.get(event, 'data.completed');

    if (_.isBoolean(isCompleted)) {
      const { paymentType } = this.props;

      if (paymentType === PAYMENT_TYPE.SUBSCRIPTION) {
        const result = isCompleted ? 'success' : 'failed';

        this.props.biEvent('widget.subscriptionPopUp.Completed', { result });
      }

      closeModal();
    }
  }

  @autobind
  @notForPreview
  openPaymentPopup() {
    const { appSettings, videoId, currentSiteUser, channelData } = this.props;
    const { paymentType } = this.state;
    const { siteUrl, instance } = appSettings;

    bi.logWidgetSystem(biPurchaseEventNames[paymentType]);

    const paymentOptions = {
      memberId: currentSiteUser.id,
      channelId: channelData.id,
      type: paymentType,
      ref: siteUrl,
      instance,
    };

    if (paymentType !== PAYMENT_TYPE.SUBSCRIPTION) {
      paymentOptions.refItemId = videoId;
      paymentOptions.ref = getUrlToVideo(this.video, appSettings);
    }

    paymentsHelper.paypal(paymentOptions);
  }

  @autobind
  updatePaymentType(paymentType) {
    this.setState({
      paymentType,
    });
  }

  getDealInfoByType(type) {
    switch (type) {
      case PAYMENT_TYPE.PURCHASE:
        return dealInfoHelpers.getSalePriceInfo(this.video.dealInfo);
      case PAYMENT_TYPE.RENT:
        return dealInfoHelpers.getRentPriceInfo(this.video.dealInfo);
      case PAYMENT_TYPE.MEMBER_ONLY:
        return {};
      default:
      case PAYMENT_TYPE.SUBSCRIPTION:
        return dealInfoHelpers.getSubscriptionPriceInfo(this.props.channelData.dealInfo);
    }
  }

  getSinglePaymentType(paymentDealInfo) {
    return (
      <div styleName="single-button-text">
        {this.getPaymentTextByDealInfo(paymentDealInfo)}
      </div>
    );
  }

  getPaymentTextByDealInfo(paymentDealInfo) {
    if (!paymentDealInfo) {
      return null;
    }

    switch (paymentDealInfo.type) {
      case PAYMENT_TYPE.PURCHASE:
        return (
          <FormattedMessage {...translations.purchase}
                            values={{
                              price: <FormattedNumber value={paymentDealInfo.price}
                                                      style="currency"
                                                      currency={paymentDealInfo.currency} />,
                            }} />
        );
      case PAYMENT_TYPE.RENT:
        return (
          <FormattedMessage {...translations.rent}
                            values={{
                              price: <FormattedNumber value={paymentDealInfo.price}
                                                      style="currency"
                                                      currency={paymentDealInfo.currency} />,
                            }} />
        );
      default:
      case PAYMENT_TYPE.SUBSCRIPTION:
        return (
          <FormattedMessage {...translations.subscriptionMonth}
                            values={{
                              price: <FormattedNumber value={paymentDealInfo.price}
                                                      style="currency"
                                                      currency={paymentDealInfo.currency} />,
                            }} />
        );
    }
  }

  get title() {
    const { paymentType } = this.state;

    let title;
    if (paymentType === PAYMENT_TYPE.SUBSCRIPTION) {
      title = this.props.channelData.title;
    } else {
      title = this.video.title;
    }

    return (
      <div ref={node => (this.titleNode = node)}
           styleName="title"
           title={title}>
        {title}
      </div>
    );
  }

  get video() {
    const { videoId, videoByIds } = this.props;
    return videoByIds[videoId];
  }

  get subscription() {
    const subscriptionInfo = dealInfoHelpers.getSubscriptionPriceInfo(this.props.channelData.dealInfo);
    return this.getSinglePaymentType(subscriptionInfo);
  }

  get buttons() {
    const { paymentType } = this.state;

    const rentDealInfo = dealInfoHelpers.getRentPriceInfo(this.video.dealInfo);
    const purchaseDealInfo = dealInfoHelpers.getSalePriceInfo(this.video.dealInfo);

    if (!rentDealInfo || !purchaseDealInfo) {
      return this.getSinglePaymentType(rentDealInfo || purchaseDealInfo);
    }

    const buttons = [
      {
        label: this.getPaymentTextByDealInfo(rentDealInfo),
        key: PAYMENT_TYPE.RENT,
        className: 'qa-widget-payment-form-rent-button',
      },
      {
        label: this.getPaymentTextByDealInfo(purchaseDealInfo),
        key: PAYMENT_TYPE.PURCHASE,
        className: 'qa-widget-payment-form-purchase-button',
      },
    ];

    return (
      <ButtonsGroup styleName="buttons"
                    buttons={buttons}
                    defaultActive={paymentType}
                    disableTheme
                    onChange={this.updatePaymentType} />
    );
  }

  get paymentTypeWrapper() {
    const { paymentType } = this.state;

    if (paymentType === PAYMENT_TYPE.SUBSCRIPTION) {
      return this.subscription;
    }

    if (paymentType === PAYMENT_TYPE.MEMBER_ONLY) {
      return this.memberOnlyBenefits;
    }

    return this.buttons;
  }

  get memberOnlyBenefits() {
    return (
      <div styleName="single-button-text">
        <FormattedMessage {...translations.memberOnlyBenefits} />
      </div>
    );
  }

  get permissions() {
    const { paymentType } = this.state;
    const dealInfo = this.getDealInfoByType(paymentType);

    return _.map(PAYMENT_PERMS[paymentType], permTitle => (
      <div styleName="permission"
           key={permTitle}>
        <Icon styleName="icon"
              name="arrow-right-filled" />
        <FormattedMessage {...translations[permTitle]}
                          values={dealInfo} />
      </div>
    ));
  }

  getCoverUrlFrom(item) {
    return item.customCoverUrl || item.coverUrl;
  }

  get coverUrl() {
    switch (this.state.paymentType) {
      case PAYMENT_TYPE.PURCHASE:
      case PAYMENT_TYPE.RENT:
      case PAYMENT_TYPE.MEMBER_ONLY:
        return this.getCoverUrlFrom(this.video);
      default:
        return this.getCoverUrlFrom(this.props.channelData);
    }
  }

  @autobind
  handleUserLogin() {
    const { paymentType } = this.state;
    const { onUserLogin, onBeforePageRefresh } = this.props;

    onUserLogin();

    onBeforePageRefresh({
      paymentType,
      selectedVideoId: this.video.id,
    });
  }

  render() {
    const { intl: { formatMessage }, onUserLogout, className } = this.props;
    const { paymentType } = this.state;

    if (!this.video && paymentType !== PAYMENT_TYPE.SUBSCRIPTION) {
      return null;
    }

    return (
      <section styleName="wrapper"
               className={classnames('qa-widget-payments-form', className)}>
        <div styleName="subscribe-part">
          <ResizedImage styleName="image"
                        src={this.coverUrl}
                        width={COVER_WIDTH}
                        height={COVER_HEIGHT} />

          <div styleName="content">
            {this.title}

            <div styleName="type-wrapper">
              {this.paymentTypeWrapper}
            </div>

            <div styleName="permissions-wrapper">
              {this.permissions}
            </div>
          </div>
        </div>
        <div styleName="auth-part">
          <AccountForm proceedText={formatMessage(translations.proceedPaypal)}
                       paymentType={paymentType}
                       proceedAction={this.openPaymentPopup}
                       onUserLogin={this.handleUserLogin}
                       onUserLogout={onUserLogout} />
        </div>
      </section>
    );
  }
}



// WEBPACK FOOTER //
// ./src/widget/components/payments/payment-form.js